import { Badge } from '@design-system';
import { X } from 'lucide-react';

import {
  FEATURE_BENEFITS_LABELS,
  type CombinedFilterType,
  type FilterName,
  type InflationTypeFilterValue,
} from '@/features/shop-insurance';

import { formatCurrency } from '@client/lib';
import { UNDERWRITING_STATUS_CONFIG } from '@shared';

interface AdjustedQuotesBannerProps {
  premiumValue: number;
  appliedFilters: FilterName[];
  benefitDurationValue: CombinedFilterType;
  inflationProtectionValue: CombinedFilterType;
  inflationTypeFilterValue?: InflationTypeFilterValue;
  selectedEligibilityFilters?: string[];
  onRemoveEligibilityFilter?: (filter: string) => void;
  selectedFeatureBenefitsFilters?: string[];
  onRemoveFeatureBenefitsFilter?: (filter: string) => void;
  hiddenCarriers?: string[];
  onRemoveHiddenCarrier?: (carrier: string) => void;
}

export function AdjustedQuotesBanner({
  premiumValue,
  appliedFilters,
  benefitDurationValue,
  inflationProtectionValue,
  inflationTypeFilterValue,
  selectedEligibilityFilters = [],
  onRemoveEligibilityFilter,
  selectedFeatureBenefitsFilters = [],
  onRemoveFeatureBenefitsFilter,
  hiddenCarriers = [],
  onRemoveHiddenCarrier,
}: AdjustedQuotesBannerProps) {
  function getFilterLabel(filter: FilterName) {
    switch (filter) {
      case 'premium':
        return `${formatCurrency(premiumValue)} Premium`;
      case 'inflationProtection':
        if (inflationProtectionValue.type === 'combined') {
          return `${inflationProtectionValue.combined[0]}%-${inflationProtectionValue.combined[1]}% Inflation`;
        } else {
          return `AOB: ${inflationProtectionValue.aob[0]}%-${inflationProtectionValue.aob[1]}%, COB: ${inflationProtectionValue.cob[0]}%-${inflationProtectionValue.cob[1]}% Inflation`;
        }
      case 'inflationType':
        return `${inflationTypeFilterValue === 'compound' ? 'Compound' : 'Simple'} Inflation`;
      case 'benefitDuration':
        if (benefitDurationValue.type === 'combined') {
          return `${benefitDurationValue.combined[0]}-${benefitDurationValue.combined[1]} Years Duration`;
        } else {
          return `AOB: ${benefitDurationValue.aob[0]}-${benefitDurationValue.aob[1]}, COB: ${benefitDurationValue.cob[0]}-${benefitDurationValue.cob[1]} Years Duration`;
        }
      default:
        return '';
    }
  }

  function getFeatureBenefitsLabel(filterKey: string) {
    return FEATURE_BENEFITS_LABELS[filterKey] || filterKey;
  }

  const filterOrder: FilterName[] = ['premium', 'inflationProtection', 'inflationType', 'benefitDuration'];

  const sortedFilters = appliedFilters.sort((filterA, filterB) => {
    return filterOrder.indexOf(filterA) - filterOrder.indexOf(filterB);
  });

  return (
    <div className="flex items-center gap-2 rounded-md border p-2.5">
      <p className="text-foreground ml-0.5 shrink-0 text-xs font-semibold uppercase tracking-wide">
        Active Filters:
      </p>
      <div className="flex flex-wrap gap-2">
        {/* Regular filters */}
        {sortedFilters.map(filter => (
          <Badge key={filter}>{getFilterLabel(filter)}</Badge>
        ))}
        {/* Eligibility filters */}
        {selectedEligibilityFilters.map(filter => {
          const config =
            UNDERWRITING_STATUS_CONFIG[filter as keyof typeof UNDERWRITING_STATUS_CONFIG];
          const filterLabel = config?.label || filter;

          return (
            <Badge key={filter}>
              {filterLabel}
              {onRemoveEligibilityFilter && (
                <button
                  onClick={() => onRemoveEligibilityFilter(filter)}
                  className="inline-flex"
                  aria-label={`Remove ${filterLabel} filter`}
                  type="button"
                >
                  <X className="size-3" />
                </button>
              )}
            </Badge>
          );
        })}
        {/* Feature Benefits filters */}
        {selectedFeatureBenefitsFilters.map(filter => {
          const filterLabel = getFeatureBenefitsLabel(filter);
          return (
            <Badge key={filter}>
              {filterLabel}
              {onRemoveFeatureBenefitsFilter && (
                <button
                  onClick={() => onRemoveFeatureBenefitsFilter(filter)}
                  className="inline-flex"
                  aria-label={`Remove ${filterLabel} filter`}
                  type="button"
                >
                  <X className="size-3" />
                </button>
              )}
            </Badge>
          );
        })}
        {/* Hidden carriers */}
        {hiddenCarriers.map(carrier => (
          <Badge key={carrier}>
            {`${carrier} hidden`}
            {onRemoveHiddenCarrier && (
              <button
                onClick={() => onRemoveHiddenCarrier(carrier)}
                className="inline-flex"
                aria-label={`Unhide ${carrier}`}
                type="button"
              >
                <X className="size-3" />
              </button>
            )}
          </Badge>
        ))}
      </div>
    </div>
  );
}
